import React, { useState, useEffect } from 'react';
import { scheduleService } from '../services/scheduleService';
import ScheduleCard from './ScheduleCard';
import ScheduleWizard from './ScheduleWizard';
import Dismissible from './common/Dismissible';
import { usePlan } from '../hooks/usePlan';
import { getPlanLimits } from '../constants/planLimits';
import { openPricingModal } from '../utils/pricingModal';

function ScheduleManager({ user }) {
  const { plan, status, resolved } = usePlan();
  const planLimits = getPlanLimits(plan);
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [filter, setFilter] = useState('active'); // 'all', 'active', 'paused'
  const [createNotice, setCreateNotice] = useState(null); // transient banner after schedule creation

  // Load schedules on mount and set up polling
  useEffect(() => {
    loadSchedules();
    
    // Poll for schedule updates every 30 seconds
    // This ensures RSS feed URLs appear after generation
    const pollInterval = setInterval(() => {
      loadSchedules(true); // Silent refresh (no loading state change)
    }, 30000); // 30 seconds
    
    return () => clearInterval(pollInterval);
  }, []);

  const loadSchedules = async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const data = await scheduleService.listSchedules();
      console.log('📊 Raw schedule data:', data);
      console.log('📊 Schedule statuses:', data.map(s => ({ id: s.scheduleId, status: s.status, name: s.scheduleName })));
      
      // Filter out deleted schedules (defensive - backend should already filter these)
      const nonDeletedSchedules = data.filter(s => s.status !== 'deleted');
      console.log(`📊 Filtered to ${nonDeletedSchedules.length} non-deleted schedules`);
      
      setSchedules(nonDeletedSchedules);
    } catch (error) {
      console.error('Failed to load schedules:', error);
      if (!silent) {
        setError(error.message);
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const handleScheduleCreated = (newSchedule) => {
    setSchedules(prev => [newSchedule, ...prev]);
    setShowWizard(false);

    // Schedule creation stays allowed while waitlisted (§4.7/§9 of the free-tier-cap-enforcement
    // plan) — but the scheduler silently skips generation for it, so tell the user right away
    // rather than letting them discover it later from a schedule that never produces episodes.
    if (status === 'waitlisted') {
      setCreateNotice(
        "Schedule created — but since you're on the free-tier waitlist, it'll stay paused until a slot opens. Upgrade to Plus to start generating right away."
      );
      setTimeout(() => setCreateNotice(null), 10000);
    } else if (resolved) {
      // Immediate-first-episode notice (docs/plans/2026-08-04-immediate-first-episode.md). The
      // backend stores nextRunTime ~now so the next scheduler tick (<=15 min) starts generating the
      // first episode; it counts toward the plan's daily cap, which resets at midnight UTC. Wording
      // avoids "today" (the cap is UTC-day, not the user's local day) and points at the ready-email
      // rather than promising an on-screen countdown. Gated on `resolved` so a still-loading usePlan
      // never shows this to a waitlisted user (they get the waitlist branch above once resolved).
      const perDay = planLimits.epsPerDay;
      const capText = plan === 'free'
        ? 'It counts toward your Free-plan limit of 1 episode per day'
        : `It counts toward your daily limit of ${perDay} episodes`;
      setCreateNotice(
        `Schedule created! Your first episode is starting to generate now (usually ready within about 15 minutes) — we'll email you when it's ready. ${capText}, which resets at midnight UTC.`
      );
      setTimeout(() => setCreateNotice(null), 14000);
    }
  };

  const handleScheduleUpdated = (updatedSchedule) => {
    setSchedules(prev =>
      prev.map(s => s.scheduleId === updatedSchedule.scheduleId ? updatedSchedule : s)
    );
  };

  const handleScheduleDeleted = (scheduleId) => {
    setSchedules(prev => prev.filter(s => s.scheduleId !== scheduleId));
  };

  const filteredSchedules = schedules.filter(s => {
    if (filter === 'all') return s.status !== 'deleted';
    return s.status === filter;
  });

  // Plan-aware limits (§ Topic 2 build plan). Only restrict once `resolved` is true — usePlan
  // defaults to 'plus' while loading/on error, and the backend is the real enforcement point
  // regardless of what these gates compute.
  const activeSchedules = schedules.filter(s => s.status !== 'deleted');
  const enabledDailyRuns = activeSchedules.reduce(
    (sum, s) => sum + (Array.isArray(s.refreshTimes) ? s.refreshTimes.length : 0),
    0
  );
  const atMaxSchedules = activeSchedules.length >= planLimits.maxSchedules;
  const atMaxDailyRuns = resolved && enabledDailyRuns >= planLimits.maxDailyRuns;
  const overDailyRunLimit = resolved && enabledDailyRuns > planLimits.maxDailyRuns;
  const createBlocked = atMaxSchedules || atMaxDailyRuns;
  const createBlockedReason = atMaxSchedules
    ? (plan === 'free'
        ? 'Free plan: 1 schedule. Upgrade to create more, or delete your existing schedule first.'
        : `Maximum of ${planLimits.maxSchedules} schedules reached (active + paused). Delete a schedule to create a new one.`)
    : atMaxDailyRuns
      ? `Your plan allows ${planLimits.maxDailyRuns} scheduled episode${planLimits.maxDailyRuns === 1 ? '' : 's'} per day, and your existing schedules already use all of it. Free up a refresh time or upgrade to create another.`
      : 'Create a new podcast schedule';

  const handleCreateClick = () => {
    if (createBlocked) {
      alert(`⚠️ ${atMaxSchedules ? 'Schedule Limit Reached' : 'Daily Episode Limit Reached'}\n\n${createBlockedReason}`);
      return;
    }
    setShowWizard(true);
  };

  if (isLoading) {
    return (
      <div className="component-card">
        <div className="loading-state">
          <p>⏳ Loading schedules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="component-card">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div className="schedule-manager-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h2 style={{ margin: 0, flex: '1 1 auto' }}>📅 Schedule Management</h2>
          <button
            onClick={handleCreateClick}
            disabled={createBlocked}
            title={createBlockedReason}
            className="btn btn-primary"
            style={{ minWidth: '160px' }}
          >
            ➕ Create Schedule
          </button>
        </div>

        {/* Plan copy: what the plan allows per day */}
        <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.75rem' }}>
          {plan === 'free' ? 'Free plan: 1 schedule, 1 episode per day.' : `Your plan: ${planLimits.maxDailyRuns} scheduled episodes per day.`}{' '}
          {plan === 'free' && (
            <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
              Upgrade
            </button>
          )}{plan === 'free' && ' to schedule more episodes or multiple refresh times.'}
        </div>

        {/* Maximum schedules warning banner */}
        {atMaxSchedules && (
          <div style={{
            padding: '1rem',
            background: '#fffbeb',
            border: '2px solid #F9C500',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.95rem',
            color: '#92400e'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>⚠️</span>
              <div>
                <strong>Schedule Limit Reached</strong>
                <div style={{ marginTop: '0.25rem' }}>
                  {plan === 'free'
                    ? 'The Free plan includes 1 schedule. Upgrade to create more, or delete your existing schedule to start a new one.'
                    : `You have reached the maximum of ${planLimits.maxSchedules} schedules (active + paused combined). To create a new schedule, please delete an existing one.`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Over-plan daily-run cleanup banner: grandfathered accounts (Option A) that configured
            more daily runs than their current plan generates. Extra runs are silently skipped by
            the scheduler; further create/update saves are rejected until this is resolved. */}
        {overDailyRunLimit && (
          <div style={{
            padding: '1rem',
            background: '#fed7d7',
            border: '2px solid #fc8181',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.95rem',
            color: '#742a2a'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>⚠️</span>
              <div>
                <strong>Your schedules exceed your plan's daily limit</strong>
                <div style={{ marginTop: '0.25rem' }}>
                  Your current schedules include more daily runs than your plan generates. Extra runs are skipped.
                  Edit your schedules to match your plan, or{' '}
                  <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
                    upgrade
                  </button>.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info banner */}
        <Dismissible id="schedules-automation-info" title="Automated Podcast Generation" userSub={user?.username}>
          <div className="dismissible__reserve-right" style={{
            padding: '1rem',
            background: '#e6fffa',
            border: '1px solid #81e6d9',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#234e52'
          }}>
            <strong>🤖 Automated Podcast Generation:</strong> Schedules automatically generate podcasts at specified times.
            Configure your news sources, audio settings, and refresh times to create your perfect news podcast workflow.
          </div>
        </Dismissible>
      </div>

      {createNotice && (
        <div style={{
          padding: '1rem',
          background: '#fff8f0',
          border: '1px solid #ECB554',
          borderRadius: '8px',
          color: '#7b4a12',
          marginBottom: '1rem'
        }}>
          ⏳ {createNotice}{' '}
          {plan === 'free' && (
            <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
              Upgrade
            </button>
          )}
        </div>
      )}

      {error && (
        <div style={{
          padding: '1rem',
          background: '#fed7d7',
          border: '1px solid #fc8181',
          borderRadius: '8px',
          color: '#742a2a',
          marginBottom: '1rem'
        }}>
          ⚠️ {error}
          <button
            onClick={loadSchedules}
            style={{
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              background: '#fc8181',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="schedule-filter-buttons" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0', flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilter('active')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'transparent',
            borderBottom: filter === 'active' ? '3px solid var(--color-primary)' : 'none',
            color: filter === 'active' ? 'var(--color-primary)' : '#718096',
            fontWeight: filter === 'active' ? '600' : '500',
            cursor: 'pointer',
            marginBottom: '-2px',
            fontSize: 'clamp(0.85rem, 2vw, 1rem)',
            flex: '1 1 auto',
            minWidth: '100px'
          }}
        >
          ✅ Active ({schedules.filter(s => s.status === 'active').length})
        </button>
        <button
          onClick={() => setFilter('paused')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'transparent',
            borderBottom: filter === 'paused' ? '3px solid var(--color-primary)' : 'none',
            color: filter === 'paused' ? 'var(--color-primary)' : '#718096',
            fontWeight: filter === 'paused' ? '600' : '500',
            cursor: 'pointer',
            marginBottom: '-2px',
            fontSize: 'clamp(0.85rem, 2vw, 1rem)',
            flex: '1 1 auto',
            minWidth: '100px'
          }}
        >
          ⏸️ Paused ({schedules.filter(s => s.status === 'paused').length})
        </button>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'transparent',
            borderBottom: filter === 'all' ? '3px solid var(--color-primary)' : 'none',
            color: filter === 'all' ? 'var(--color-primary)' : '#718096',
            fontWeight: filter === 'all' ? '600' : '500',
            cursor: 'pointer',
            marginBottom: '-2px',
            fontSize: 'clamp(0.85rem, 2vw, 1rem)',
            flex: '1 1 auto',
            minWidth: '100px'
          }}
        >
          📋 All ({schedules.filter(s => s.status !== 'deleted').length})
        </button>
      </div>

      {/* Schedules List */}
      {filteredSchedules.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">📅</div>
          <h3 className="empty-state__title">
            {filter === 'active' && 'No Active Schedules'}
            {filter === 'paused' && 'No Paused Schedules'}
            {filter === 'all' && 'No Schedules Yet'}
          </h3>
          <p className="empty-state__text">
            {filter === 'all'
              ? 'Create your first automated podcast schedule to get started'
              : `You don't have any ${filter} schedules`
            }
          </p>
          {filter === 'all' && (
            <button
              onClick={handleCreateClick}
              disabled={createBlocked}
              title={createBlocked ? createBlockedReason : 'Create your first podcast schedule'}
              className="btn btn-primary"
            >
              ➕ Create Your First Schedule
            </button>
          )}
        </div>
      ) : (
        <div>
          {filteredSchedules.map(schedule => (
            <ScheduleCard
              key={schedule.scheduleId}
              schedule={schedule}
              user={user}
              onUpdate={handleScheduleUpdated}
              onDelete={handleScheduleDeleted}
            />
          ))}
        </div>
      )}

      {/* Wizard Modal */}
      {showWizard && (
        <ScheduleWizard
          user={user}
          onComplete={handleScheduleCreated}
          onCancel={() => setShowWizard(false)}
          existingEnabledRuns={enabledDailyRuns}
        />
      )}

      {/* Help Section */}
      <Dismissible id="schedules-how" title="How Schedules Work" userSub={user?.username}>
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'var(--color-sky-light)',
          borderRadius: '8px',
          border: '1px solid var(--color-border)'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#2d3748', fontSize: '1.1rem' }}>💡 How Schedules Work</h3>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#4a5568', lineHeight: '1.8' }}>
            <li>
              <strong>Automated Generation:</strong> Schedules automatically generate podcasts at your specified times
            </li>
            <li>
              <strong>Timezone Aware:</strong> All times use your selected timezone and handle DST automatically
            </li>
            <li>
              <strong>News Sources:</strong> Podcasts are created from your configured news sources
            </li>
            <li>
              <strong>Pause Anytime:</strong> Temporarily pause schedules without losing your configuration
            </li>
            <li>
              <strong>Limit:</strong> {plan === 'free'
                ? 'Free plan: 1 schedule, generating 1 episode per day'
                : `Maximum ${planLimits.maxSchedules} schedules per account (active + paused combined), generating up to ${planLimits.maxDailyRuns} scheduled episode${planLimits.maxDailyRuns === 1 ? '' : 's'} per day across all of them`}
            </li>
          </ul>
        </div>
      </Dismissible>
    </div>
  );
}

export default ScheduleManager;
